/**
 * Toast Provider - Reusable notifications (success / error / warning /
 * loading), rendered in a viewport that stays exposed while a dialog is open.
 *  */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';

import { ToastItem } from './toast/ToastItem';
import type { ToastData as Toast } from './toast/toast.types';

interface ToastContextType {
  toast: (options: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  /** Shows a loading toast with spinner. Returns id to pass to dismiss/update. Does NOT auto-dismiss. */
  loading: (title: string, description?: string) => string;
  /** Updates an existing toast (e.g. loading progress). Call with id from loading(). */
  update: (id: string, title: string, description?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * A modal Dialog aria-hides everything outside it but `[aria-live]` nodes
 * (aria-hidden's hideOthers), so the viewport list carries aria-live — "off",
 * because Radix already announces each toast through its own status; a
 * polite list would say it twice. The toasts stay perceivable (and F8 still
 * reaches them) while a dialog is open.
 */
const EXEMPT_FROM_MODAL_HIDING = { 'aria-live': 'off' } as const;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((options: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...options, id }]);

    if (!options.persistent) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    }
    return id;
  }, []);

  const toast = useCallback(
    (options: Omit<Toast, 'id'>) => addToast(options),
    [addToast],
  );

  const success = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'success' }),
    [addToast],
  );

  const error = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'error' }),
    [addToast],
  );

  const warning = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'warning' }),
    [addToast],
  );

  const loading = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'loading', persistent: true }),
    [addToast],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const update = useCallback(
    (id: string, title: string, description?: string) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, title, description } : t)),
      );
    },
    [],
  );

  const dismiss = useCallback(
    (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, loading, update, dismiss }}>
      <ToastPrimitives.Provider swipeDirection="right">
        {children}

        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClosed={removeToast} />
        ))}

        <ToastPrimitives.Viewport {...EXEMPT_FROM_MODAL_HIDING} className="fixed bottom-4 right-4 z-[250] flex flex-col-reverse gap-2 p-0" />
      </ToastPrimitives.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
